import { ChatTransport } from './chatTransport.js'

/**
 * Surfaces a v1-compatible `ochatbot` object for components that still
 * call `ochatbot.callAPI(...)` (e.g. ButtonsMessage, vote buttons).
 *
 * The facade owns a `ChatTransport` instance and forwards user actions
 * through it. Each public method maps to the legacy signature so we
 * don't have to rewrite call sites:
 *
 *   ochatbot.callAPI(text, buttonText, interactive, attachment, onMsg, onErr)
 *   ochatbot.setUserId(id)
 *   ochatbot.setChatServer(url)
 *   ochatbot.setCurrentPage(url)
 *   ochatbot.setIsWebAi(bool)
 *   ochatbot.setTypingIndicatorHandler(setter)
 *
 * Additions over v1 (so streaming integrates cleanly):
 *   ochatbot.onToken(handler) / offToken(handler)
 *   ochatbot.onTyping(handler) / offTyping(handler)
 *   ochatbot.onError(handler)
 *   ochatbot.onAbort(handler)
 *   ochatbot.abort()
 */
export function createOchatbot(initial = {}) {
  const transport = new ChatTransport(initial)
  let typingHandler = null
  let messageHandler = null
  const tokenHandlers = new Set()
  const typingHandlers = new Set()
  const streamEndHandlers = new Set()
  const citationHandlers = new Set()
  const suggestedPromptHandlers = new Set()
  const errorHandlers = new Set()
  const abortHandlers = new Set()

  const facade = {
    transport,
    setUserId(userId) {
      transport.configure({ userId })
    },
    setChatServer(url) {
      transport.configure({ chatServer: url })
    },
    setStreamingEndpoint(url) {
      transport.configure({ streamingEndpoint: url })
    },
    setFallbackEndpoint(url) {
      transport.configure({ fallbackEndpoint: url })
    },
    setCurrentPage(url) {
      transport.configure({ currentPage: url })
    },
    setIsWebAi(value) {
      transport.configure({ isWebAi: Boolean(value) })
    },
    setSessionId(sessionId) {
      transport.configure({ sessionId })
    },
    setAgentId(agentId) {
      transport.configure({ agentId })
    },
    setMasterKey(masterKey) {
      transport.configure({ masterKey })
    },
    setAuthToken(token) {
      transport.configure({ authToken: token })
    },
    setStreamingEnabled(enabled) {
      transport.configure({ streamingEnabled: Boolean(enabled) })
    },
    setTypingIndicatorHandler(setter) {
      typingHandler = setter
    },
    setMessageHandler(handler) {
      messageHandler = handler
    },
    onToken(fn) {
      tokenHandlers.add(fn)
      return () => tokenHandlers.delete(fn)
    },
    onTyping(fn) {
      typingHandlers.add(fn)
      return () => typingHandlers.delete(fn)
    },
    onStreamEnd(fn) {
      streamEndHandlers.add(fn)
      return () => streamEndHandlers.delete(fn)
    },
    onCitations(fn) {
      citationHandlers.add(fn)
      return () => citationHandlers.delete(fn)
    },
    onSuggestedPrompts(fn) {
      suggestedPromptHandlers.add(fn)
      return () => suggestedPromptHandlers.delete(fn)
    },
    onError(fn) {
      errorHandlers.add(fn)
      return () => errorHandlers.delete(fn)
    },
    onAbort(fn) {
      abortHandlers.add(fn)
      return () => abortHandlers.delete(fn)
    },
    abort() {
      transport.abort()
    },
    /**
     * Legacy `callAPI` signature kept verbatim so the message-rendering
     * components (`Buttons`, `MessageBubble`, etc.) don't need to know
     * the transport changed. `onMsg` receives full structured messages
     * (per-message), and the facade also fans out streaming tokens to
     * any registered `onToken` handlers.
     */
    callAPI(text, buttonText = '', interactive = false, attachment = null, onMsg = null, onErr = null) {
      return transport.send(
        { text, buttonText, interactive, attachment },
        {
          onTyping: (value) => {
            typingHandler?.(value)
            for (const fn of typingHandlers) fn(value)
          },
          onToken: (event) => {
            for (const fn of tokenHandlers) fn(event)
          },
          onStreamEnd: (payload) => {
            for (const fn of streamEndHandlers) fn(payload)
          },
          onCitations: (payload) => {
            for (const fn of citationHandlers) fn(payload)
          },
          onSuggestedPrompts: (payload) => {
            for (const fn of suggestedPromptHandlers) fn(payload)
          },
          onMessage: (msg) => {
            const stamped = { ...msg, from: msg.from || 'chatbot' }
            if (typeof onMsg === 'function') onMsg(stamped)
            messageHandler?.(stamped)
          },
          onError: (err) => {
            if (typeof onErr === 'function') onErr(err)
            for (const fn of errorHandlers) fn(err)
          },
          onAbort: () => {
            for (const fn of abortHandlers) fn()
          },
        },
      )
    },
  }
  return facade
}
